import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Trash2, CirclePlus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";
import { FilePreviewModal } from "@/components/FilePreviewModal";

// Service and Utils
import { postResult } from "@/services/finance";
import type { CreateFinancialRequest } from "@/types/finance";
// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/result/add/"
)({
  component: AddResult,
});

function AddResult() {
  const { toast } = useToast();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = localStorage.getItem("companyId");
  const [year, setYear] = React.useState<number | undefined>(() => {
    const storedYear = localStorage.getItem("year");
    if (!storedYear) return undefined;
    const parsed = Number(storedYear);
    return isNaN(parsed) ? undefined : parsed;
  });
  const [docFile, setDocFile] = React.useState("");

  const [financialDetails, setFinancialDetails] = React.useState<
    Array<{
      finDetailType:
        | "INITIAL"
        | "OPERATING"
        | "PRE_TAX"
        | "NET_OPERATING"
        | "AFTER_TAX"
        | "ANNUAL_NET";
      financialType: "INCOME" | "EXPENSE";
      name: string;
      amount: number | string;
      remark: string;
      amountForBackend?: number;
    }>
  >([
    // Initial income/expense
    {
      finDetailType: "INITIAL",
      financialType: "EXPENSE",
      name: "ລາຍຮັບຈາກກິດຈະການປົກກະຕິ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "INITIAL",
      financialType: "EXPENSE",
      name: "ຕົ້ນທຶນຂາຍ",
      amount: 0,
      remark: "",
    },
    // Operating income/expense
    {
      finDetailType: "OPERATING",
      financialType: "INCOME",
      name: "ລາຍຮັບອື່ນໆ ຈາກການດໍາເນີນງານປົກກະຕິ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "OPERATING",
      financialType: "EXPENSE",
      name: "ຕົ້ນທຶນຈາກການຈໍາໜ່າຍ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "OPERATING",
      financialType: "EXPENSE",
      name: "ຕົ້ນທຶນບໍລິຫານ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "OPERATING",
      financialType: "EXPENSE",
      name: "ຄ່າໃຊ້ຈ່າຍອື່ນໆ ໃນທຸລະກິດ",
      amount: 0,
      remark: "",
    },
    // Pre-tax income/expense
    {
      finDetailType: "PRE_TAX",
      financialType: "INCOME",
      name: "ລາຍຮັບການເງິນ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "PRE_TAX",
      financialType: "EXPENSE",
      name: "ລາຍຈ່າຍການເງິນ",
      amount: 0,
      remark: "",
    },

    // Net operating income
    {
      finDetailType: "NET_OPERATING",
      financialType: "EXPENSE",
      name: "ອາກອນຕ້ອງຈ່າຍ ບ້ວງຜົນໄດ້ຮັບປົກກະຕິ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "NET_OPERATING",
      financialType: "EXPENSE",
      name: "ອາກອນເຍື້ອນຊໍາລະ",
      amount: 0,
      remark: "",
    },

    // After-tax income/expense
    {
      finDetailType: "AFTER_TAX",
      financialType: "INCOME",
      name: "ທີ່ເປັນສ່ວນຂອງ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "AFTER_TAX",
      financialType: "INCOME",
      name: "ພູດສ່ວນ ຂອງຜົນປະໂຫຍດສ່ວນໜ້ອຍ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "AFTER_TAX",
      financialType: "INCOME",
      name: "ພູດສ່ວນ ຂອງກຸ່ມ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "AFTER_TAX",
      financialType: "INCOME",
      name: "ລາຍຮັບສັງລວມອື່ນໆ",
      amount: 0,
      remark: "",
    },
    {
      finDetailType: "AFTER_TAX",
      financialType: "EXPENSE",
      name: "ລາຍຈ່າຍສັງລວມອື່ນໆ",
      amount: 0,
      remark: "",
    },
  ]);

  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.FINANCIAL_REPORT);

  const mutation = useMutation({
    mutationFn: (data: CreateFinancialRequest) => postResult(data),
    onSuccess: () => {
      toast({
        title: "ສຳເລັດ",
        description: "ເພີ່ມຂໍ້ມູນສຳເລັດ",
        variant: "success",
      });

      queryClient.invalidateQueries({
        queryKey: ["getResult", companyId, year],
      });
      localStorage.setItem("year", JSON.stringify(year));
      navigate({
        to: "/report/finance/company/result",
        search: { refId: undefined as string | undefined },
      });
    },
    onError: (error: Error) => {
      console.log(error);

      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;

    if (!docFile) {
      toast({
        title: "ບັນທຶກລົ້ມເຫຼວ",
        description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
        variant: "destructive",
      });
      return;
    }

    // Modify financialDetails to replace `amount` with `amountForBackend`
    const modifiedFinancialDetails = financialDetails.map(
      ({ amountForBackend, ...rest }) => ({
        ...rest,
        amount: amountForBackend || 0, // Use the value from amountForBackend for the 'amount' field
      })
    );

    const data = {
      companyId,
      yearly: year!,
      docFile,
      financialDetails: modifiedFinancialDetails, // Use the modified financialDetails
    };

    console.log("Form Data", data);

    mutation.mutate(data);
  };

  const getDetailsByType = (type: string) =>
    financialDetails.filter((detail) => detail.finDetailType === type);

  const updateDetail = (
    type: string,
    typeIndex: number,
    field: string,
    value: string | number
  ) => {
    const newDetails = [...financialDetails];
    const actualIndex = financialDetails.findIndex(
      (detail) =>
        detail.finDetailType === type &&
        financialDetails
          .filter((d) => d.finDetailType === type)
          .indexOf(detail) === typeIndex
    );

    if (actualIndex !== -1) {
      if (field === "amount") {
        const stringValue = String(value);

        // Allow empty input
        if (!stringValue) {
          newDetails[actualIndex] = {
            ...newDetails[actualIndex],
            amount: "",
            amountForBackend: 0,
          };
          setFinancialDetails(newDetails);
          return;
        }

        // Remove existing formatting
        const rawValue = stringValue.replace(/,/g, "");

        // Check if the input matches our pattern (including decimals and parentheses)
        if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
          // Handle decimal places
          let processedValue = rawValue;
          if (rawValue.includes(".")) {
            const [integerPart, decimalPart] = rawValue.split(".");
            if (decimalPart && decimalPart.replace(")", "").length > 2) {
              const limitedDecimal = decimalPart.replace(")", "").slice(0, 2);
              processedValue = `${integerPart}.${limitedDecimal}${
                rawValue.endsWith(")") ? ")" : ""
              }`;
            }
          }

          // Convert to number for backend
          const numericValue = processedValue.replace(/[()]/g, "");
          const numericAmount = Number(numericValue);

          newDetails[actualIndex] = {
            ...newDetails[actualIndex],
            amount: processedValue,
            amountForBackend: processedValue.startsWith("(")
              ? -numericAmount
              : numericAmount,
            financialType: processedValue.startsWith("(")
              ? "EXPENSE"
              : "INCOME",
          };
          setFinancialDetails(newDetails);
        }
      } else {
        newDetails[actualIndex] = {
          ...newDetails[actualIndex],
          [field]: value,
        };
        setFinancialDetails(newDetails);
      }
    }
  };

  const addNewRow = (
    type:
      | "INITIAL"
      | "OPERATING"
      | "PRE_TAX"
      | "NET_OPERATING"
      | "AFTER_TAX"
      | "ANNUAL_NET",
    financialType: "INCOME" | "EXPENSE" = "INCOME"
  ) => {
    setFinancialDetails([
      ...financialDetails,
      {
        finDetailType: type,
        financialType,
        name: "",
        amount: 0,
        remark: "",
      },
    ]);
  };

  const removeRow = (type: string, index: number) => {
    const itemsOfType = getDetailsByType(type);
    const newDetails = financialDetails.filter(
      (item) => item !== itemsOfType[index]
    );
    setFinancialDetails(newDetails);
  };

  const getSectionTitle = (type: string) => {
    const titles: Record<string, string> = {
      INITIAL: "ຜົນໄດ້ຮັບເບື້ອງຕົ້ນ",
      OPERATING: "ຜົນໄດ້ຮັບໃນທຸລະກິດ",
      PRE_TAX: "ຜົນໄດ້ຮັບ ກ່ອນເສຍອາກອນ",
      NET_OPERATING: "ຜົນໄດ້ຮັບ ສຸດທິຈາກການດໍາເນີນງານ",
      AFTER_TAX: "ຜົນໄດ້ຮັບສັງລວມອື່ນໆ ຫຼັງອາກອນ",
      // ANNUAL_NET: "ກຳໄລປະຈຳປີ",
    };
    return titles[type];
  };

  const getSectionSUM = (type: string) => {
    const titles: Record<string, React.ReactNode> = {
      OPERATING: <p>{formatNumber(totalOperating + totalInitial)}</p>,
      PRE_TAX: (
        <p>{formatNumber(totalOperating + totalInitial + totalPreTax)}</p>
      ),
      NET_OPERATING: (
        <p>
          {formatNumber(
            totalOperating + totalInitial + totalPreTax + totalNetOperating
          )}
        </p>
      ),
      AFTER_TAX: (
        <p>
          {formatNumber(
            // totalOperating +
            // 	totalInitial +
            // 	totalPreTax +
            // 	totalNetOperating +
            totalAfterTax
          )}
        </p>
      ),
      // ANNUAL_NET: "ກຳໄລປະຈຳປີ",
    };
    return titles[type];
  };

  function formatNumber(value: string | number) {
    if (!value && value !== 0) return "";

    const stringValue = String(value || "");
    const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
    const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

    // Handle the case when user is typing a decimal point
    if (numericValue.endsWith(".")) {
      return isNegative ? `(${numericValue})` : numericValue;
    }

    // Split number into integer and decimal parts
    const [integerPart, decimalPart] = numericValue.split(".");

    // Format integer part with commas
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Handle decimal part - limit to 2 decimal places
    const formattedDecimal = decimalPart ? decimalPart.slice(0, 2) : undefined;

    // Combine with decimal part if it exists
    const formattedValue =
      formattedDecimal !== undefined
        ? `${formattedInteger}.${formattedDecimal}`
        : formattedInteger;

    return isNegative ? `(${formattedValue})` : formattedValue;
  }

  const calculateTotal = React.useCallback(
    (type: string): number => {
      return financialDetails
        .filter((detail) => detail.finDetailType === type)
        .reduce((sum, detail) => {
          // Use the numeric value from amountForBackend if available; otherwise, fallback to 0.
          const value = detail.amountForBackend
            ? Number(detail.amountForBackend)
            : 0;

          // For expenses, the amountForBackend should already be negative
          // so we just add it to the sum (no need to subtract)
          return sum + value;
        }, 0);
    },
    [financialDetails]
  );

  const totalInitial = React.useMemo(
    () => calculateTotal("INITIAL"),
    [calculateTotal]
  );

  const totalOperating = React.useMemo(
    () => calculateTotal("OPERATING"),
    [calculateTotal]
  );

  const totalPreTax = React.useMemo(
    () => calculateTotal("PRE_TAX"),
    [calculateTotal]
  );

  const totalNetOperating = React.useMemo(
    () => calculateTotal("NET_OPERATING"),
    [calculateTotal]
  );

  const totalAfterTax = React.useMemo(
    () => calculateTotal("AFTER_TAX"),
    [calculateTotal]
  );

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between">
            <div>
              <CardTitle>ເພີ່ມຂໍ້ມູນຜົນດຳເນີນງານ</CardTitle>
              <CardDescription>ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Year Input */}
            <div className="flex justify-between items-center w-full">
              {/* <div className="flex flex-col gap-2 mb-4">
								<Label>ປະຈຳປີ</Label>
								<div className="flex flex-col gap-y-2 items-center">
									<Input
										type="number"
										value={year}
										onChange={handleYearChange}
										placeholder="ປ້ອນປີ..."
										min={1900}
										max={2100}
										required
									/>
									{yearError && (
										<span className="text-sm text-red-500">{yearError}</span>
									)}
								</div>
							</div> */}

              <div className="flex flex-col gap-2 mb-4">
                <Label htmlFor="yearly">
                  ປີ <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={year ? String(year) : ""}
                  onValueChange={(value) => {
                    setYear(Number(value));
                  }}
                  required
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="ກະລຸນາເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(
                      { length: new Date().getFullYear() - 1974 },
                      (_, i) => (
                        <SelectItem
                          key={i}
                          value={(new Date().getFullYear() - i).toString()}
                        >
                          {new Date().getFullYear() - i}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* <div className="">
								<small className="text-yellow-500">ໝາຍເຫດ: ການປ້ອນຕົວເລກ</small>

								<p className="text-sm">
									ຖ້າຫາກຕົວເລກເປັນຄ່າລົບ ຄວນປ້ອນ (ຕົວເລກ), ຕົວຢ່າງ: (5000)
								</p>
								<p className="text-sm">
									ຖ້າຫາກຕົວເລກເປັນຄ່າບວກ ຄວນປ້ອນ ຕົວເລກ, ຕົວຢ່່າງ: 5000
								</p>
							</div> */}
            </div>

            {/* Results Table */}
            <Table>
              <TableHeader>
                <TableRow className="[&>*]:border [&>*]:font-bold [&>*]:text-black    ">
                  <TableHead>ເນື້ອໃນລາຍງານ</TableHead>
                  <TableHead>ມູນຄ່າຍັງເຫຼືອປີນີ້</TableHead>
                  <TableHead>ໝາຍເຫດ</TableHead>
                  <TableHead>ຈັດການ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Initial */}

                {getDetailsByType("INITIAL").map((detail, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={detail.name}
                        onChange={(e) =>
                          updateDetail("INITIAL", index, "name", e.target.value)
                        }
                        readOnly={index < 2}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={formatNumber(detail.amount)}
                        onChange={(e) =>
                          updateDetail(
                            "INITIAL",
                            index,
                            "amount",
                            e.target.value
                          )
                        }
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={detail.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) =>
                          updateDetail(
                            "INITIAL",
                            index,
                            "remark",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {index > 1 ? (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeRow("INITIAL", index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  className="flex gap-x-2 items-center p-2 my-2 w-auto text-blue-600 border-2 border-blue-600 hover:bg-blue-500 hover:text-white"
                  size="icon"
                  onClick={() => addNewRow("INITIAL")}
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>
                <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black">
                  <TableCell>ຜົນໄດ້ຮັບເບື້ອງຕົ້ນ</TableCell>
                  <TableCell className="text-right">
                    {formatNumber(totalInitial.toFixed(2))}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell> </TableCell>
                </TableRow>
                {/* Apply the same pattern to all other sections */}
                {(
                  [
                    "OPERATING",
                    "PRE_TAX",
                    "NET_OPERATING",
                    "AFTER_TAX",
                    // "ANNUAL_NET",
                  ] as const
                ).map((sectionType) => (
                  <React.Fragment key={sectionType}>
                    {getDetailsByType(sectionType).map((detail, index) => (
                      <TableRow key={index} className="[&>*]:border">
                        <TableCell className="flex gap-x-1 items-center">
                          {index + 1}.
                          <Input
                            value={detail.name}
                            onChange={(e) =>
                              updateDetail(
                                sectionType,
                                index,
                                "name",
                                e.target.value
                              )
                            }
                            readOnly={
                              sectionType === "OPERATING"
                                ? index < 4
                                : sectionType === "PRE_TAX"
                                ? index < 2
                                : sectionType === "NET_OPERATING"
                                ? index < 2
                                : sectionType === "AFTER_TAX"
                                ? index < 5
                                : false
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="text"
                            value={formatNumber(detail.amount)}
                            onChange={(e) =>
                              updateDetail(
                                sectionType,
                                index,
                                "amount",
                                e.target.value
                              )
                            }
                            className="text-right"
                            inputMode="numeric"
                            placeholder="1,000,000"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={detail.remark}
                            placeholder="ໝາຍເຫດ"
                            onChange={(e) =>
                              updateDetail(
                                sectionType,
                                index,
                                "remark",
                                e.target.value
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {sectionType === "OPERATING" &&
                          index < 4 ? null : sectionType === "PRE_TAX" &&
                            index < 2 ? null : sectionType ===
                              "NET_OPERATING" &&
                            index < 2 ? null : sectionType === "AFTER_TAX" &&
                            index < 5 ? null : (
                            <Button
                              type="button"
                              variant={"ghost"}
                              size="icon"
                              onClick={() => removeRow(sectionType, index)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex gap-x-2 items-center p-2 my-2 w-auto text-blue-600 border-2 border-blue-600 hover:bg-blue-500 hover:text-white"
                      size="icon"
                      onClick={() => addNewRow(sectionType)}
                    >
                      <CirclePlus className="w-4 h-4" />
                      ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                    </Button>
                    <TableRow className="bg-gray-100 [&>*]:font-bold [&>*]:text-black [&>*]:border">
                      <TableCell>{getSectionTitle(sectionType)}</TableCell>
                      <TableCell className="text-right">
                        {getSectionSUM(sectionType)}
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell> </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                  <TableCell className="whitespace-nowrap border">
                    ຜົນໄດ້ຮັບສຸດທິໃນປີ
                  </TableCell>
                  <TableCell className="text-right border">
                    {formatNumber(
                      totalOperating +
                        totalInitial +
                        totalPreTax +
                        totalNetOperating +
                        totalAfterTax
                    )}
                  </TableCell>
                  {/* last year */}
                  <TableCell className="text-left border">-</TableCell>
                  <TableCell className="text-left border">- </TableCell>
                </TableRow>
                <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                  <TableCell className="whitespace-nowrap border">
                    ທີ່ເປັນສ່ວນຂອງ
                  </TableCell>
                  <TableCell className="text-left border">-</TableCell>
                  {/* last year */}
                  <TableCell className="text-left border">-</TableCell>
                  <TableCell className="text-left border">- </TableCell>
                </TableRow>
                <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                  <TableCell className="whitespace-nowrap border">
                    ພູດສ່ວນ ຂອງຜົນປະໂຫຍດສ່ວນນ້ອຍ(1)
                  </TableCell>
                  <TableCell className="text-left border">-</TableCell>
                  {/* last year */}
                  <TableCell className="text-left border">-</TableCell>
                  <TableCell className="text-left border">- </TableCell>
                </TableRow>
                <TableRow className="font-bold bg-gray-100 [&>*]:border [&>*]:border-gray-300">
                  <TableCell className="whitespace-nowrap border">
                    ພູດສ່ວນ ຂອງກຸ່ມ(1)
                  </TableCell>
                  <TableCell className="text-left border">-</TableCell>
                  {/* last year */}
                  <TableCell className="text-left border">-</TableCell>
                  <TableCell className="text-left border">- </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* File Upload */}
            <div className="flex gap-x-1 items-center mt-4">
              <FileUpload
                currentFile={docFile}
                onFileUpload={(file) => {
                  console.log(file, "File Uploaded");
                  setDocFile(file);
                }}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-x-2 justify-end mt-4">
              <Button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/report/finance",
                    search: { page: 1, startYear: 0, endYear: 0 },
                  })
                }
                variant={"outline"}
              >
                ຍົກເລີກ
              </Button>
              <Button
                disabled={mutation.isPending}
                type="submit"
                className="flex gap-x-2 items-center"
              >
                {mutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    ບັນທຶກ
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
